/**
 * Stamps a `data-scrolling` attribute on whichever element is actively
 * scrolling (user or programmatic, e.g. chat auto-follow), clearing it
 * shortly after the last scroll event. global.css uses the attribute to
 * reveal the custom scrollbar thumb only while content is moving.
 *
 * WHY: native macOS overlay scrollbars expand into a full track when the
 * pointer crosses the gutter and re-flash on every programmatic scroll, so
 * during streaming the expanded bar can stick around indefinitely. Styling
 * `::-webkit-scrollbar` opts out of the native overlay entirely; this module
 * restores the "thumb appears while scrolling, then fades" behavior.
 */
export const SCROLLBAR_HIDE_DELAY_MS = 800;

const isElement = (target: EventTarget | null): target is Element =>
  target !== null && typeof (target as Element).setAttribute === 'function';

export function initScrollbarActivity(
  doc: Document = document,
  hideDelayMs: number = SCROLLBAR_HIDE_DELAY_MS
): () => void {
  const timers = new Map<Element, ReturnType<typeof setTimeout>>();

  const onScroll = (event: Event) => {
    // Document-level scrolls report the document as target; the attribute
    // belongs on the root element so CSS can match it.
    const el = isElement(event.target) ? event.target : doc.documentElement;
    if (!el) return;

    if (!el.hasAttribute('data-scrolling')) {
      el.setAttribute('data-scrolling', '');
    }
    const pending = timers.get(el);
    if (pending) clearTimeout(pending);
    timers.set(
      el,
      setTimeout(() => {
        el.removeAttribute('data-scrolling');
        timers.delete(el);
      }, hideDelayMs)
    );
  };

  // Capture phase: `scroll` does not bubble, but capture descends into every scroller.
  doc.addEventListener('scroll', onScroll, { capture: true, passive: true });

  return () => {
    doc.removeEventListener('scroll', onScroll, { capture: true });
    for (const [el, timer] of timers) {
      clearTimeout(timer);
      el.removeAttribute('data-scrolling');
    }
    timers.clear();
  };
}
